<script setup>
// Conectar uma rede social (decisão Ivan 08/10: uma rede = um card = um botão
// que faz tudo). A conexão serve para publicar e ver métricas no Estúdio
// Social e, nas redes com caixa por essa conexão (TikTok, Google, LinkedIn,
// Instagram na conexão única), também para a caixa de entrada.
// `compact`: só o botão e os avisos, para quem já tem o próprio cartão.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import marketingAPI from 'dashboard/api/marketing';
import socialAPI from 'dashboard/api/social';
import { SOCIAL_PLATFORMS } from 'dashboard/components-next/social/constants';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  platform: { type: String, required: true },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(['connected']);

const { t } = useI18n();

const meta = computed(
  () => SOCIAL_PLATFORMS.find(pl => pl.key === props.platform) || {}
);
const networkName = computed(() =>
  t(`SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${props.platform.toUpperCase()}`)
);

const known = ref(null); // ids já conectados desta rede antes do clique
const limitReached = ref(false);
const connecting = ref(false);
const waiting = ref(false);
const error = ref('');

// Conectar não pede módulo (decisão Ivan 08/10): cada conta conectada ocupa
// 1 canal do plano; sem vaga, o botão fica travado com o aviso.
async function loadAccounts() {
  try {
    const { data } = await socialAPI.accounts();
    const limit = data.limit ?? 0;
    limitReached.value = limit > 0 && (data.connected_count ?? 0) >= limit;
    return (data.accounts || []).filter(
      a => a.platform === props.platform && a.status === 'connected'
    );
  } catch {
    return null;
  }
}

async function connect() {
  if (connecting.value) return;
  error.value = '';
  connecting.value = true;
  const w = 600;
  const h = 740;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  // Popup nasce no gesto do clique; a URL entra depois (anti-bloqueio).
  const popup = window.open(
    '',
    'chatsuite-social-connect',
    `popup=yes,width=${w},height=${h},left=${left},top=${top}`
  );
  try {
    const before = await loadAccounts();
    known.value = new Set((before || []).map(a => a.id));
    const { data } = await socialAPI.connectUrl(props.platform);
    if (data?.url && popup && !popup.closed) popup.location = data.url;
    else if (data?.url) window.open(data.url, '_blank');
    waiting.value = true;
  } catch (e) {
    if (popup) popup.close();
    error.value =
      e.response?.data?.error ||
      e.response?.data?.errors?.[0] ||
      t('SOCIAL_STUDIO.ACCOUNTS.GENERIC_ERROR');
  } finally {
    connecting.value = false;
  }
}

// O OAuth termina fora daqui: ao voltar o foco, reconcilia e vê se entrou
// conta nova desta rede.
async function onFocus() {
  if (!waiting.value) return;
  await marketingAPI.connectionsSync().catch(() => {});
  const accounts = await loadAccounts();
  const fresh = (accounts || []).filter(a => !known.value?.has(a.id));
  if (fresh.length) {
    waiting.value = false;
    emit('connected', fresh[0]);
  }
}

onMounted(async () => {
  await loadAccounts();
  window.addEventListener('focus', onFocus);
});
onBeforeUnmount(() => window.removeEventListener('focus', onFocus));
</script>

<template>
  <div
    class="flex flex-col"
    :class="
      compact
        ? 'items-start'
        : 'items-center px-8 py-8 text-center rounded-2xl outline outline-1 outline-n-weak'
    "
  >
    <template v-if="!compact">
      <span
        class="size-10 rounded-xl flex items-center justify-center"
        :class="meta.badgeClass"
      >
        <span class="size-6 text-white" :class="meta.icon" />
      </span>
      <h6 class="mt-4 text-lg font-medium text-n-slate-12">
        {{
          title ||
          t('INBOX_MGMT.ADD.SOCIAL_NETWORK.TITLE', { network: networkName })
        }}
      </h6>
      <p class="max-w-md mt-2 text-sm text-n-slate-11">
        {{ description || t('INBOX_MGMT.ADD.SOCIAL_NETWORK.DESC') }}
      </p>
    </template>
    <p
      v-if="limitReached"
      class="mt-3 text-xs text-n-amber-11 flex items-center gap-1.5"
    >
      <span class="i-lucide-triangle-alert size-3.5" />
      {{ t('INBOX_MGMT.ADD.SOCIAL_NETWORK.LIMIT_REACHED') }}
    </p>
    <Button
      :class="compact ? 'mt-3' : 'mt-5'"
      :icon="meta.icon"
      :label="
        t('INBOX_MGMT.ADD.SOCIAL_NETWORK.CONNECT', { network: networkName })
      "
      :is-loading="connecting"
      :disabled="connecting || limitReached"
      @click="connect"
    />
    <p v-if="waiting" class="mt-3 text-xs text-n-slate-10">
      {{ t('INBOX_MGMT.ADD.SOCIAL_NETWORK.WAITING') }}
    </p>
    <p v-if="error" class="mt-3 text-xs text-n-ruby-11">{{ error }}</p>
  </div>
</template>
